<script setup>
import SidebarCard from '@/components/cards/SidebarCard.vue';
import AppFooter from '@/components/AppFooter.vue';
import NavTab from '@/components/navigation/NavTab.vue';
import SIdeBarPostList from '@/components/cards/SIdeBarPostList.vue';
import DefaultLayout from '@/layouts/DefaultLayout.vue';
import AppHeader from '@/components/headers/AppHeader.vue';
import MainHeaderComponent from '@/components/headers/MainHeaderComponent.vue';
import ImageCard from '@/components/cards/ImageCard.vue';
import { useBusinessStore } from '@/stores/business';
import {computed, onMounted, watch, ref} from 'vue';
import { useMiscStore } from '@/stores/misc';
import {useRoute} from 'vue-router';
import { useContributorStore } from '@/stores/contributor';
import SidebarContributorPostList from '@/components/cards/SidebarContributorPostList.vue';
import TopContributorComponent from '@/components/cards/TopContributorComponent.vue';
import CustomCardImageContent from "@/components/cards/CustomCardImageContent.vue";
import BestOfBestCard from "@/components/cards/BestOfBestCard.vue";

const store = useBusinessStore();
const misc = useMiscStore();
const route = useRoute();
const contributorStore = useContributorStore();
const contributor = computed(() => contributorStore.contributor);
const currentSector = computed(() => misc.currentSector);
const isLoading = ref(false);

// Main data fetching function
const fetchData = async (force = false) => {
  if (!route.params.sector || !currentSector.value) {
    return;
  }
  
  // Skip API calls for special pages with negative IDs
  if (currentSector.value.id < 0) {
    loading.value = false;
    console.log('Skipping API calls for special page:', currentSector.value.name);
    return;
  }
  
  isLoading.value = true;
  const sectorId = currentSector.value.id;
  const sortBy = route.query.sortBy || 'created_at';
  
  try {
    if (route.params.username && contributor.value?.id) {
      // Handle contributor sector data
      await Promise.all([
        contributorStore.fetchBusinessesForSector(contributor.value.id, sectorId, sortBy),
        contributorStore.fetchMostVotedBusinesses(contributor.value.id, sectorId),
        contributorStore.fetchTopRatedBusinesses(contributor.value.id, sectorId)
      ]);
    } else {
      // Handle regular sector data
      await Promise.all([
        store.fetchBusinessesForSector(sectorId, sortBy),
        store.fetchMostVoted(sectorId),
        store.fetchTopRated(sectorId),
        store.fetchBestOfVoted(sectorId),
        store.fetchBestOfRated(sectorId)
      ]);
    }
  } catch (error) {
    console.error('Error fetching data:', error);
  } finally {
    isLoading.value = false;
  }
};

// Fetch contributor data if username is in route
if (route.params.username) {
  contributorStore.fetchContributor(route.params.username);
}

// Consolidates all data fetching logic
const loadData = async () => {
  const sectorSlug = route.params.sector;
  if (!sectorSlug) return;

  // Ensure the current sector is set before fetching data
  if (!currentSector.value || currentSector.value.slug !== sectorSlug) {
    await misc.updateCurrentSector(sectorSlug);
  }

  // Now that the sector is set, fetch the data
  await fetchData();
};

// Watch for sector changes to load data
watch(
  () => route.params.sector,
  (newSector, oldSector) => {
    if (newSector && newSector !== oldSector) {
      loadData();
    }
  }
);

// Watch for sorting changes to refetch data
watch(
  () => route.query.sortBy,
  (newSortBy, oldSortBy) => {
    if (newSortBy !== oldSortBy) {
      fetchData();
    }
  }
);

// Initial data fetch on component mount
onMounted(async () => {
  contributorStore.fetchPopular('contributions');
  await loadData();
});

let businesses = computed(() => store.businesses),
 topRated = computed(() => store.topRated),
 bestOfRated = computed(() => store.bestOfRated),
 mostVoted = computed(() => store.mostVoted),
 bestOfVoted = computed(() => store.bestOfVoted),
 loading = computed(() => misc.loading),
 loadingTopRated = computed(() => store.loadingTopRated),
 loadingMostVoted = computed(() => store.loadingMostVoted),
 topContributors = computed(() => contributorStore.popular || contributorStore.topRated);

if (route.params.username) {
 businesses = computed(() => contributorStore.businesses),
 topRated = computed(() => contributorStore.topRatedBusinesses),
 mostVoted = computed(() => contributorStore.mostVotedBusinesses),
     loading = computed(() => misc.loading),
 loadingTopRated = computed(() => contributorStore.loadingTopRatedBusinesses),
 loadingMostVoted = computed(() => contributorStore.loadingMostVotedBusinesses);
}
</script>

<template>
  <DefaultLayout :loading="loading">
    <template #header>
      <AppHeader
        v-if="currentSector"
        :bg-image="currentSector.backgroundImage"
        :height="'50vh'"
      >
        <template #content>
          <MainHeaderComponent
            :title="currentSector.name"
            :subtitle="currentSector.description"
            :sector-description="null"
            used-by="Sector"
          />
        </template>
      </AppHeader>
    </template>
    <template #main>
      <NavTab
        :businesses="businesses"
        :contributor="contributor"
      />
    </template>
    <template #aside>
      <ImageCard
        v-if="!bestOfRated && !bestOfVoted"
        :image="{ url: currentSector?.backgroundImage, alt: currentSector?.name }"
      />
      <BestOfBestCard
        v-if="bestOfRated"
        :image="{
          url: bestOfRated?.baseImageUrl
        }"
      >
        <template #body>
          <CustomCardImageContent
            type="rated"
            :business="bestOfRated"
          />
        </template>
      </BestOfBestCard>
      <BestOfBestCard
        v-if="bestOfVoted"
        :image="{
          url: bestOfVoted?.baseImageUrl
        }"
      >
        <template #body>
          <CustomCardImageContent
            type="voted"
            :business="bestOfVoted"
          />
        </template>
      </BestOfBestCard>
      <SidebarCard
        :as-content="topRated.length > 0"
        :loading="loadingTopRated"
      >
        <template #header-title>
          Top Rated Businesses in {{ currentSector?.name }}
        </template>
        <template #body>
          <SidebarContributorPostList
            v-if="contributor?.id"
            :businesses="topRated"
          />
          <SIdeBarPostList
            v-else
            :businesses="topRated"
          />
        </template>
      </SidebarCard>

      <SidebarCard
        :as-content="(topRated.length && contributor?.id) > 0 || mostVoted.length > 0"
        :loading="loadingMostVoted"
      >
        <template #header-title>
          Most Voted Businesses in {{ currentSector?.name }}
        </template>
        <template #body>
          <SidebarContributorPostList
            v-if="contributor?.id"
            :businesses="topRated"
          />
          <SIdeBarPostList
            v-else
            :businesses="mostVoted"
          />
        </template>
      </SidebarCard>

      <SidebarCard :as-content="topContributors?.items?.length > 0">
        <template #header-title>
          Top Contributors
        </template>
        <template #body>
          <TopContributorComponent :contributors="topContributors" />
        </template>
        <template #footer>
          <div class="border-t border-gray-200 pt-0.5 pb-2 px-4 text-right text-red-600">
            <router-link
              :to="`/contributors`"
              class="text-xs font-semibold uppercase"
            >
              View all contributors
              <ArrowRightIcon class="inline h-4 w-4 ml-1" />
            </router-link>
          </div>
        </template>
      </SidebarCard>
    </template>
  </DefaultLayout>
  <AppFooter />
</template>

<style scoped></style>
